import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { Card } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const faqs = [
  {
    question: 'How does the customized subscription work?',
    answer: 'Simply tell us your travel sector (departure and arrival cities), number of tickets you need, and your travel frequency. We\'ll create a personalized subscription plan with pricing tailored specifically for your route and requirements.'
  },
  {
    question: 'What information do I need to provide?',
    answer: 'You need to provide: your contact details, departure and arrival cities (sector), number of tickets required, and how frequently you plan to travel (one-time, monthly, quarterly, etc.). You can also add any specific preferences or requirements.'
  },
  {
    question: 'How long does it take to get a quote?',
    answer: 'Our team will review your requirements and send you a personalized quote within 24 hours via email. The quote will include pricing, subscription duration, and all included benefits.'
  },
  {
    question: 'Is the pricing same for all routes?',
    answer: 'No, pricing is customized based on your specific sector (route), number of tickets, and travel frequency. Popular routes and higher frequency plans get better rates. Each plan is tailored to offer maximum value for your specific needs.'
  },
  {
    question: 'Can I change my route after subscribing?',
    answer: 'Route changes depend on your specific subscription plan. When you receive your personalized quote, it will clearly mention the flexibility options available for your plan. Contact our support team for assistance with modifications.'
  },
  {
    question: 'Which airlines and routes are covered?',
    answer: 'All major Indian airlines are covered.'
  },
  {
    question: 'What are the payment terms?',
    answer: 'Payment terms will be included in your personalized quote. Typically, we offer flexible payment options including one-time upfront payment or installment plans based on your subscription duration.'
  },
  {
    question: 'Is there a refund policy?',
    answer: 'Refund policies are specific to each subscription plan and will be clearly mentioned in your personalized quote. Generally, we offer flexible cancellation for individual bookings as per plan benefits.'
  },
  {
    question: 'Do I get priority support?',
    answer: 'Yes! All AirVia subscribers get priority customer support via email at hello@airviaindia.com. We respond to all queries within 24 hours and provide dedicated assistance for all your travel needs.'
  },
  {
    question: 'Can I get a plan for multiple routes?',
    answer: 'Absolutely! When filling the interest form, mention all your routes in the additional notes section. We can create multi-sector plans or separate plans for different routes based on your requirements.'
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-24 pb-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" data-testid="faq-title">Frequently Asked Questions</h1>
            <p className="text-lg text-slate-600">Everything you need to know about AirVia</p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <Card className="border-slate-200 overflow-hidden" data-testid={`faq-item-${index}`}>
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                    data-testid={`faq-question-btn-${index}`}
                  >
                    <h3 className="text-lg font-semibold text-slate-900 pr-4">{faq.question}</h3>
                    {openIndex === index ? (
                      <ChevronUp className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-600 flex-shrink-0" />
                    )}
                  </button>
                  {openIndex === index && (
                    <div className="px-6 pb-6">
                      <p className="text-slate-600 leading-relaxed" data-testid={`faq-answer-${index}`}>{faq.answer}</p>
                    </div>
                  )}
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12 text-center"
          >
            <Card className="p-8 border-slate-200 bg-slate-900 text-white">
              <h2 className="text-2xl font-bold mb-3">Still have questions?</h2>
              <p className="text-slate-300 mb-6">Our support team is here to help 24/7</p>
              <a href="/contact" className="inline-block px-8 py-3 bg-amber-600 text-white rounded-full font-semibold hover:bg-amber-700 transition-colors" data-testid="faq-contact-link">
                Contact Support
              </a>
            </Card>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
